import type { WebLocale } from '@/enums/web-locale';
import type { RouteRecordRaw } from 'vue-router';

import { CollectionType } from '@/enums/collection-type';
import { Domain } from '@/routing/helpers/subdomain-helper';

import { makeAppRoutes, PageType } from '@/routing/config';
import { makeEditorialRoutes } from '@/routing/editorial';
import { makeUserRoutes } from '@/features/user-profile/routes';
import { makeSportRoutes } from '@/routing/sports';
import { makeRedirectRoutes } from '@/routing/redirect';
import { makeStaticRoutes } from '@/routing/static';
import { makeRentalListRoutes } from '@/routing/lists';

const routesCache = new Map<string, RouteRecordRaw[]>();
export function createRoutes(webLocale: WebLocale, currentDomain: Domain = Domain.MAIN) {
	const cacheKey = `${webLocale}|${currentDomain}`;
	const cached = routesCache.get(cacheKey);
	if (cached) return cached;
	// Build all route groups
	const homeRoutes = makeHomeRoute();
	const rentalRoutes = makeRentalListRoutes(webLocale);
	const sportsRoutes = makeSportRoutes(webLocale);
	const editorialRoutes = makeEditorialRoutes(webLocale);
	const appRoutes = makeAppRoutes(webLocale, currentDomain);
	const redirectRoutes = makeRedirectRoutes(webLocale);
	const userRoutes = makeUserRoutes(webLocale);
	const staticRoutes = makeStaticRoutes(webLocale);

	const campaignRoutes: RouteRecordRaw[] = [
		{
			name: 'app.campaign.dach',
			path: '/dach/campaign',
			component: () => import('@/pages/landing-pages/campaigns/DachCampaign.vue'),
		},
		{
			name: 'app.rent.signin',
			path: '/rent/signin',
			component: () => import('@/pages/RentSignin.vue'),
		},
	];

	// Order routes by priority based on current domain
	// Current subdomain's routes MUST come first to match before cross-domain routes with same paths
	let prioritizedRoutes: RouteRecordRaw[];

	if (currentDomain === Domain.SPORTS) {
		prioritizedRoutes = [
			...sportsRoutes,
			...homeRoutes,
			...rentalRoutes,
			...appRoutes,
			...redirectRoutes,
			...userRoutes,
			...campaignRoutes,
			...staticRoutes,
			...editorialRoutes,
		];
	}
	else if (currentDomain === Domain.EDITORIAL) {
		prioritizedRoutes = [
			...editorialRoutes,
			...homeRoutes,
			...rentalRoutes,
			...appRoutes,
			...redirectRoutes,
			...userRoutes,
			...campaignRoutes,
			...staticRoutes,
			...sportsRoutes,
		];
	}
	else {
		prioritizedRoutes = [
			...homeRoutes,
			...rentalRoutes,
			...appRoutes,
			...redirectRoutes,
			...userRoutes,
			...campaignRoutes,
			...staticRoutes,
			...sportsRoutes,
			...editorialRoutes,
		];
	}
	routesCache.set(cacheKey, prioritizedRoutes);
	// Return all routes - domain switching is handled in navigation guards
	return prioritizedRoutes;
}

function makeHomeRoute(): RouteRecordRaw[] {
	return [
		{
			name: 'app.home',
			path: '/',
			component: () => import('@/pages/home/HomePage.vue'),
			meta: {
				collectionType: CollectionType.GLOBAL,
				hasFilterBar: false,
				initialRouteState: {},
				contexts: [],
				tab: 'home',
				pageType: PageType.HOME,
			},
		},
	];
}

export function jw_changeLocale(webLocale: WebLocale, currentDomain: Domain = Domain.MAIN) {
	const router = useRouter();
	const routes = createRoutes(webLocale, currentDomain);
	routes.forEach(route => router.addRoute(route));
}
